import React from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Helper function to generate URL-friendly slug from title
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
};

import { getTimeAgoShort as getTimeAgo } from '../lib/dateUtils';

// Maximum articles to show in sidebar before overflow to More Stories
const MAX_SIDEBAR_ARTICLES = 25;

const LeftSidebar = ({ articles = [], onArticleClick, onOverflowArticles }) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';
  
  // Get localized title
  const getTitle = (article) => {
    if (isKinyarwanda && article.title_rw) return article.title_rw;
    return article.title_en || article.title || 'Untitled';
  };

  // Sort articles by published date (newest first)
  const sortedArticles = [...articles].sort((a, b) => {
    const dateA = new Date(a.published_at || 0);
    const dateB = new Date(b.published_at || 0);
    return dateB - dateA;
  });

  // Split articles: sidebar shows first N, rest overflow to More Stories
  const sidebarArticles = sortedArticles.slice(0, MAX_SIDEBAR_ARTICLES);
  const overflowArticles = sortedArticles.slice(MAX_SIDEBAR_ARTICLES);

  // Notify parent about overflow articles (for More Stories section)
  React.useEffect(() => {
    if (onOverflowArticles) {
      onOverflowArticles(overflowArticles);
    }
  }, [overflowArticles.length, onOverflowArticles]);

  if (!articles || articles.length === 0) {
    return (
      <aside className="hidden lg:block w-64 xl:w-72 border-r border-gray-200 dark:border-gray-800 pr-4">
        <div className="space-y-4">
          <div className="pb-3 section-underline-red">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2" style={{ fontFamily: "'Public Sans', sans-serif" }}>
              <Clock className="h-3.5 w-3.5 text-[#D90429]" />
              {isKinyarwanda ? 'Amakuru Mashya' : 'Latest News'}
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            {isKinyarwanda ? 'Nta makuru ariho' : 'No stories yet'}
          </p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="hidden lg:block w-64 xl:w-72 border-r border-gray-200 dark:border-gray-800 pr-4">
      {/* Content flows naturally - no sticky, no scroll */}
      <div className="space-y-0">
        {/* Recent Stories Header - Professional newspaper style with red underline */}
        <div className="pb-3 mb-3 border-b-4 border-[#D90429]">
          <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2" style={{ fontFamily: "'Public Sans', sans-serif" }}>
            <Clock className="h-3.5 w-3.5 text-[#D90429]" />
            {isKinyarwanda ? 'Amakuru Mashya' : 'Latest News'}
          </h3>
        </div>
        
        {/* Articles List - Flows naturally down the page */}
        <div className="space-y-0">
          {sidebarArticles.map((article, index) => {
            const articleUrl = `/news/${generateSlug(getTitle(article))}`;
            const time = getTimeAgo(article.published_at || article.created_at || article.date);
            
            return (
              <Link
                key={article.id || index}
                to={articleUrl}
                data-testid={`recent-story-${index}`}
                onClick={(e) => {
                  if (onArticleClick) {
                    e.preventDefault();
                    onArticleClick(article);
                    return;
                  }
                  const isExternal = article.is_fetched || (article.link && !article.is_user_created);
                  if (isExternal && article.link) {
                    e.preventDefault();
                    window.open(article.link, '_blank', 'noopener,noreferrer');
                  }
                }}
                className={`flex gap-3 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors group ${
                  index !== sidebarArticles.length - 1 ? 'border-b border-gray-100 dark:border-gray-800' : ''
                }`}
              >
                {/* Timestamp */}
                <div className="flex-shrink-0 w-10 text-center pt-0.5">
                  <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
                    {time}
                  </span>
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-normal text-gray-800 dark:text-gray-100 leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-3">
                    {getTitle(article)}
                  </h4>
                  {article.source && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 block mt-1">
                      {article.source}
                    </span>
                  )}
                </div>
                
                {/* Thumbnail */}
                {article.image_url && (
                  <div className="flex-shrink-0 w-14 h-14 overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <img
                      src={article.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </Link>
            );
          })}
        </div>
        
        {/* Footer - View All link */}
        <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-800">
          <Link 
            to="/library" 
            className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline font-medium uppercase tracking-wide"
          >
            {isKinyarwanda ? 'Reba byose →' : 'View all stories →'}
          </Link>
        </div>
      </div>
    </aside>
  );
};

// Export constants for use in other components
export const SIDEBAR_MAX_ARTICLES = MAX_SIDEBAR_ARTICLES;
export default LeftSidebar;
